import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Polygon, Marker, Polyline, useMap } from 'react-leaflet';
import { Hexagon, Search, MapPin, X, Plus, Edit2, Sparkles, Loader, Check } from 'lucide-react';
import { cellToBoundary, polygonToCells, latLngToCell } from 'h3-js';
import Autocomplete from "react-google-autocomplete";
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import 'leaflet/dist/leaflet.css';
import './App.css';
import AuthPage from './AuthPage';
import PosterStudio from './PosterStudio';
import ContactWidget from './ContactWidget';
import MapSwitcher from './MapSwitcher';
import { generateAllPlaceCircles, getUnionedCircleRegions, getPlaceHexagons } from './circleUtils';
import { getPlaceCenter, createPinIcon } from './pinUtils';

function MapController({ targetBounds }) {
  const map = useMap();
  useEffect(() => {
    if (targetBounds) {
      map.flyToBounds(targetBounds, { duration: 1.5, padding: [50, 50] });
    }
  }, [targetBounds, map]);
  return null;
}

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8005";
const hasGoogleKey = Boolean(process.env.REACT_APP_GOOGLE_MAPS_API_KEY && process.env.REACT_APP_GOOGLE_MAPS_API_KEY.trim() !== "");

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [maps, setMaps] = useState([]);
  const [activeMapId, setActiveMapId] = useState(null);
  const [isMapsLoading, setIsMapsLoading] = useState(false);
  const [visitedHexes, setVisitedHexes] = useState({});
  const [places, setPlaces] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [mapBounds, setMapBounds] = useState(null);
  const [mapStyle, setMapStyle] = useState("default");
  const [isStudioOpen, setIsStudioOpen] = useState(false);
  const [showUnvisited, setShowUnvisited] = useState(false);

  // Filter tabs for places list in sidebar ('all' | 'visited' | 'yet_to_visit')
  const [placesFilter, setPlacesFilter] = useState("all");

  // Pending Place confirmation card state
  const [pendingPlace, setPendingPlace] = useState(null);
  const [pendingCustomLabel, setPendingCustomLabel] = useState("");
  const [pendingStatus, setPendingStatus] = useState("visited");

  // In-line place editing state
  const [editingPlaceId, setEditingPlaceId] = useState(null);
  const [editCustomLabel, setEditCustomLabel] = useState("");
  const [editPlaceName, setEditPlaceName] = useState("");

  // Free OpenStreetMap (Nominatim) search state
  const [osmQuery, setOsmQuery] = useState("");
  const [osmResults, setOsmResults] = useState([]);
  const [isSearchingOsm, setIsSearchingOsm] = useState(false);
  const osmTimeoutRef = useRef(null);

  const getStyleDefaultOpacity = (style) => {
    if (style === "circled") return 0.8;
    if (style === "pins" || style === "pins-label") return 0.0;
    return 0.4;
  };
  const [opacity, setOpacity] = useState(getStyleDefaultOpacity("default"));

  const visitedHexesRef = useRef({});
  useEffect(() => {
    visitedHexesRef.current = visitedHexes;
  }, [visitedHexes]);

  const activeMap = useMemo(() => {
    return maps.find(m => m.id === activeMapId) || null;
  }, [maps, activeMapId]);

  // Load hexagons and places for a specific map project
  const loadMapData = async (userId, targetMapId) => {
    setIsLoading(true);
    try {
      const url = targetMapId 
        ? `${API_URL}/users/${userId}/visited?map_id=${targetMapId}`
        : `${API_URL}/users/${userId}/visited`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to load map data");
      const data = await res.json();

      const loadedHexes = {};
      if (data.hexagons) {
        data.hexagons.forEach(hexObj => {
          loadedHexes[hexObj.h3_index] = cellToBoundary(hexObj.h3_index);
        });
      }
      setVisitedHexes(loadedHexes);

      let loadedPlaces = [];
      if (data.places && data.places.length > 0) {
        loadedPlaces = data.places.map(p => ({
          ...p,
          h3Indexes: p.h3_indexes || []
        }));
      } else if (data.hexagons) {
        const groupedPlaces = {};
        data.hexagons.forEach(hexObj => {
          const placeName = hexObj.name || `Discovered Area (${hexObj.h3_index.substring(0, 6)}...)`;
          if (!groupedPlaces[placeName]) {
            groupedPlaces[placeName] = { id: placeName, name: placeName, status: 'visited', h3Indexes: [] };
          }
          groupedPlaces[placeName].h3Indexes.push(hexObj.h3_index);
        });
        loadedPlaces = Object.values(groupedPlaces).reverse();
      }
      setPlaces(loadedPlaces);

      if (Object.keys(loadedHexes).length > 0) {
        let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
        Object.values(loadedHexes).forEach(boundary => {
          boundary.forEach(([lat, lng]) => {
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
            if (lng < minLng) minLng = lng;
            if (lng > maxLng) maxLng = lng;
          });
        });
        if (minLat < maxLat && minLng < maxLng) {
          setMapBounds([[minLat, minLng], [maxLat, maxLng]]);
        }
      } else if (loadedPlaces.length > 0) {
        let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
        let validCoords = false;
        loadedPlaces.forEach(p => {
          if (p.lat != null && p.lng != null) {
            validCoords = true;
            if (p.lat < minLat) minLat = p.lat;
            if (p.lat > maxLat) maxLat = p.lat;
            if (p.lng < minLng) minLng = p.lng;
            if (p.lng > maxLng) maxLng = p.lng;
          }
        });
        if (validCoords) {
          setMapBounds([[minLat - 0.05, minLng - 0.05], [maxLat + 0.05, maxLng + 0.05]]);
        }
      }
    } catch (err) {
      console.error("API error loading map data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch user's map projects and load the active map
  useEffect(() => {
    if (!currentUser) return;
    setIsLoading(true);
    fetch(`${API_URL}/users/${currentUser.id}/maps`)
      .then(res => res.json())
      .then(data => {
        const userMaps = data.maps || [];
        setMaps(userMaps);
        const currentActiveId = data.active_map_id || (userMaps[0] ? userMaps[0].id : null);
        setActiveMapId(currentActiveId);

        const currentActiveMap = userMaps.find(m => m.id === currentActiveId);
        if (currentActiveMap && currentActiveMap.map_style) {
          const effectiveStyle = (currentActiveMap.map_style === "see-through" || currentActiveMap.map_style === "clear") ? "circled" : currentActiveMap.map_style;
          setMapStyle(effectiveStyle);
          setOpacity(getStyleDefaultOpacity(effectiveStyle));
        }

        loadMapData(currentUser.id, currentActiveId);
      })
      .catch(err => {
        console.error("Error fetching maps:", err);
        loadMapData(currentUser.id, null);
      });
  }, [currentUser]);

  // Map Project Handlers
  const handleSelectMap = async (mapId) => {
    if (!currentUser || mapId === activeMapId) return;
    setIsMapsLoading(true);
    try {
      const res = await fetch(`${API_URL}/users/${currentUser.id}/active-map/${mapId}`, {
        method: "PUT"
      });
      if (res.ok) {
        const data = await res.json();
        setActiveMapId(mapId);
        setMaps(prev => prev.map(m => ({ ...m, is_active: m.id === mapId })));
        if (data.map_style) {
          const effectiveStyle = (data.map_style === "see-through" || data.map_style === "clear") ? "circled" : data.map_style;
          setMapStyle(effectiveStyle);
          setOpacity(getStyleDefaultOpacity(effectiveStyle));
        }
        await loadMapData(currentUser.id, mapId);
      }
    } catch (err) {
      console.error("Error switching active map:", err);
    } finally {
      setIsMapsLoading(false);
    }
  };

  const handleCreateMap = async ({ name, description, map_style }) => {
    if (!currentUser) return;
    const res = await fetch(`${API_URL}/users/${currentUser.id}/maps`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, description, map_style })
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.detail || "Failed to create map project");
    }
    const newMap = await res.json();
    setMaps(prev => [newMap, ...prev.map(m => ({ ...m, is_active: false }))]);
    setActiveMapId(newMap.id);
    if (newMap.map_style) {
      const effectiveStyle = (newMap.map_style === "see-through" || newMap.map_style === "clear") ? "circled" : newMap.map_style;
      setMapStyle(effectiveStyle);
      setOpacity(getStyleDefaultOpacity(effectiveStyle));
    }
    setVisitedHexes({});
    setPlaces([]);
  };

  const handleDuplicateMap = async (mapId, newName) => {
    if (!currentUser) return;
    const res = await fetch(`${API_URL}/maps/${mapId}/duplicate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newName })
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.detail || "Failed to duplicate map project");
    }
    const clonedMap = await res.json();
    setMaps(prev => [clonedMap, ...prev.map(m => ({ ...m, is_active: false }))]);
    setActiveMapId(clonedMap.id);
    if (clonedMap.map_style) {
      const effectiveStyle = (clonedMap.map_style === "see-through" || clonedMap.map_style === "clear") ? "circled" : clonedMap.map_style;
      setMapStyle(effectiveStyle);
      setOpacity(getStyleDefaultOpacity(effectiveStyle));
    }
    await loadMapData(currentUser.id, clonedMap.id);
  };

  const handleUpdateMap = async (mapId, { name, description, map_style }) => {
    const res = await fetch(`${API_URL}/maps/${mapId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, description, map_style })
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.detail || "Failed to update map project");
    }
    const updated = await res.json();
    setMaps(prev => prev.map(m => m.id === mapId ? { ...m, ...updated } : m));
    if (mapId === activeMapId && updated.map_style) {
      const effectiveStyle = (updated.map_style === "see-through" || updated.map_style === "clear") ? "circled" : updated.map_style;
      setMapStyle(effectiveStyle);
      setOpacity(getStyleDefaultOpacity(effectiveStyle));
    }
  };

  const handleDeleteMap = async (mapId) => {
    const res = await fetch(`${API_URL}/maps/${mapId}`, {
      method: "DELETE"
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.detail || "Failed to delete map project");
    }
    const data = await res.json();
    const nextActiveId = data.active_map_id;
    setMaps(prev => prev.filter(m => m.id !== mapId).map(m => ({
      ...m,
      is_active: m.id === nextActiveId
    })));
    if (nextActiveId) {
      setActiveMapId(nextActiveId);
      const nextMap = maps.find(m => m.id === nextActiveId);
      if (nextMap && nextMap.map_style) {
        const effectiveStyle = (nextMap.map_style === "see-through" || nextMap.map_style === "clear") ? "circled" : nextMap.map_style;
        setMapStyle(effectiveStyle);
        setOpacity(getStyleDefaultOpacity(effectiveStyle));
      }
      await loadMapData(currentUser.id, nextActiveId);
    }
  };

  // Remove place from backend and update map
  const removePlace = async (id) => {
    try {
      const placeToDelete = places.find(p => p.id === id);
      const res = await fetch(`${API_URL}/users/${currentUser.id}/places/${id}`, {
        method: "DELETE"
      });
      if (res.ok) {
        setPlaces(prev => prev.filter(p => p.id !== id));
        if (placeToDelete && placeToDelete.h3Indexes) {
          setVisitedHexes(prev => {
            const next = { ...prev };
            placeToDelete.h3Indexes.forEach(hex => delete next[hex]);
            return next;
          });
        }
        if (placeToDelete) {
          const isVisited = placeToDelete.status !== 'yet_to_visit';
          setMaps(prev => prev.map(m => {
            if (m.id === activeMapId) {
              return {
                ...m,
                places_count: Math.max(0, (m.places_count || 1) - 1),
                visited_count: Math.max(0, (m.visited_count || 0) - (isVisited ? 1 : 0)),
                wishlist_count: Math.max(0, (m.wishlist_count || 0) - (!isVisited ? 1 : 0))
              };
            }
            return m;
          }));
        }
      }
    } catch (err) {
      console.error("Error deleting place:", err);
    }
  };

  // Toggle place status between 'visited' and 'yet_to_visit'
  const togglePlaceStatus = async (place) => {
    const newStatus = place.status === "yet_to_visit" ? "visited" : "yet_to_visit";
    try {
      const res = await fetch(`${API_URL}/users/${currentUser.id}/places/${place.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        const data = await res.json();
        const updatedPlace = {
          ...data.place,
          h3Indexes: data.place.h3_indexes || []
        };

        if (newStatus === "visited") {
          if (data.new_hexagons && data.new_hexagons.length > 0) {
            setVisitedHexes(prev => {
              const next = { ...prev };
              data.new_hexagons.forEach(hex => {
                next[hex] = cellToBoundary(hex);
              });
              return next;
            });
          }
        } else {
          if (place.h3Indexes && place.h3Indexes.length > 0) {
            setVisitedHexes(prev => {
              const next = { ...prev };
              place.h3Indexes.forEach(hex => delete next[hex]);
              return next;
            });
          }
        }

        setPlaces(prev => prev.map(p => p.id === place.id ? updatedPlace : p));

        setMaps(prev => prev.map(m => {
          if (m.id === activeMapId) {
            return {
              ...m,
              visited_count: newStatus === 'visited' ? (m.visited_count || 0) + 1 : Math.max(0, (m.visited_count || 1) - 1),
              wishlist_count: newStatus === 'yet_to_visit' ? (m.wishlist_count || 0) + 1 : Math.max(0, (m.wishlist_count || 1) - 1)
            };
          }
          return m;
        }));
      }
    } catch (err) {
      console.error("Error toggling place status:", err);
    }
  };

  // In-line editing
  const startEditing = (place) => {
    setEditingPlaceId(place.id);
    setEditCustomLabel(place.custom_label || "");
    setEditPlaceName(place.name || "");
  };

  const saveEditingPlace = async (id) => {
    try {
      const res = await fetch(`${API_URL}/users/${currentUser.id}/places/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          custom_label: editCustomLabel,
          name: editPlaceName
        })
      });
      if (res.ok) {
        const data = await res.json();
        setPlaces(prev => prev.map(p => p.id === id ? {
          ...p,
          custom_label: data.place.custom_label,
          name: data.place.name
        } : p));
      }
    } catch (err) {
      console.error("Error updating place:", err);
    } finally {
      setEditingPlaceId(null);
    }
  };

  const focusPlace = (place) => {
    if (place.h3Indexes && place.h3Indexes.length > 0) {
      let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
      place.h3Indexes.forEach(hex => {
        const boundary = cellToBoundary(hex);
        boundary.forEach(([lat, lng]) => {
          if (lat < minLat) minLat = lat;
          if (lat > maxLat) maxLat = lat;
          if (lng < minLng) minLng = lng;
          if (lng > maxLng) maxLng = lng;
        });
      });
      setMapBounds([[minLat, minLng], [maxLat, maxLng]]);
    } else if (place.lat != null && place.lng != null) {
      setMapBounds([
        [place.lat - 0.04, place.lng - 0.04],
        [place.lat + 0.04, place.lng + 0.04]
      ]);
    }
  };

  // Confirm adding pending place with custom label and status
  const confirmAddPlace = async () => {
    if (!pendingPlace) return;
    setIsLoading(true);
    const { name, lat, lng, north, south, east, west, source } = pendingPlace;
    const customLabel = pendingCustomLabel.trim() || null;
    const status = pendingStatus;

    try {
      let h3Indexes = [];
      if (status === "visited") {
        const polygon = [[
          [north, east],
          [north, west],
          [south, west],
          [south, east]
        ]];

        const latSpan = Math.abs(north - south);
        const lngSpan = Math.abs(east - west);
        const maxSpan = Math.max(latSpan, lngSpan);
        let res = 7;
        if (maxSpan > 8) res = 4;
        else if (maxSpan > 2) res = 5;
        else if (maxSpan > 0.5) res = 6;
        else if (maxSpan > 0.08) res = 7;
        else if (maxSpan > 0.01) res = 8;
        else res = 9;

        h3Indexes = polygonToCells(polygon, res, false);
        if (h3Indexes.length === 0 && lat != null && lng != null) {
          h3Indexes = [latLngToCell(lat, lng, res)];
        }
      }

      // Fly map to location
      if (north !== south && east !== west) {
        setMapBounds([[south, west], [north, east]]);
      } else if (lat != null && lng != null) {
        setMapBounds([[lat - 0.05, lng - 0.05], [lat + 0.05, lng + 0.05]]);
      }

      const res = await fetch(`${API_URL}/users/${currentUser.id}/places`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          map_id: activeMapId,
          name,
          custom_label: customLabel,
          status,
          lat,
          lng,
          hexagons: h3Indexes,
          source
        })
      });

      if (res.ok) {
        const savedPlace = await res.json();
        const formattedPlace = {
          ...savedPlace,
          h3Indexes: savedPlace.h3_indexes || h3Indexes
        };

        if (h3Indexes.length > 0) {
          setVisitedHexes(prev => {
            const next = { ...prev };
            h3Indexes.forEach(hex => {
              if (!next[hex]) {
                next[hex] = cellToBoundary(hex);
              }
            });
            return next;
          });
        }

        setPlaces(prev => [formattedPlace, ...prev.filter(p => p.id !== formattedPlace.id)]);

        setMaps(prev => prev.map(m => {
          if (m.id === activeMapId) {
            const isVisited = status === 'visited';
            return {
              ...m,
              places_count: (m.places_count || 0) + 1,
              visited_count: (m.visited_count || 0) + (isVisited ? 1 : 0),
              wishlist_count: (m.wishlist_count || 0) + (!isVisited ? 1 : 0)
            };
          }
          return m;
        }));
      }
    } catch (err) {
      console.error("Error creating place:", err);
    } finally {
      setIsLoading(false);
      setPendingPlace(null);
      setPendingCustomLabel("");
    }
  };

  const handlePlaceSelected = (place) => {
    if (!place || !place.geometry) return;
    const lat = place.geometry.location ? place.geometry.location.lat() : null;
    const lng = place.geometry.location ? place.geometry.location.lng() : null;
    let north = lat != null ? lat + 0.005 : 0;
    let south = lat != null ? lat - 0.005 : 0;
    let east = lng != null ? lng + 0.005 : 0;
    let west = lng != null ? lng - 0.005 : 0;

    if (place.geometry.viewport) {
      const ne = place.geometry.viewport.getNorthEast();
      const sw = place.geometry.viewport.getSouthWest();
      north = ne.lat();
      south = sw.lat();
      east = ne.lng();
      west = sw.lng();
    }
    const name = place.formatted_address || place.name;
    setPendingPlace({
      name,
      lat: lat ?? (north + south) / 2,
      lng: lng ?? (east + west) / 2,
      north,
      south,
      east,
      west,
      source: "google"
    });
    setPendingCustomLabel("");
    setPendingStatus("visited");
  };

  const handleOsmInputChange = (val) => {
    setOsmQuery(val);
    if (osmTimeoutRef.current) clearTimeout(osmTimeoutRef.current);
    if (!val || val.trim().length < 2) {
      setOsmResults([]);
      return;
    }
    osmTimeoutRef.current = setTimeout(async () => {
      setIsSearchingOsm(true);
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(val)}&format=json&limit=5&addressdetails=1`, {
          headers: { "Accept-Language": "en" }
        });
        if (res.ok) {
          const data = await res.json();
          setOsmResults(data || []);
        }
      } catch (e) {
        console.error("OSM search error:", e);
      } finally {
        setIsSearchingOsm(false);
      }
    }, 300);
  };

  const handleOsmPlaceSelected = (item) => {
    if (!item) return;
    const south = item.boundingbox ? parseFloat(item.boundingbox[0]) : parseFloat(item.lat) - 0.005;
    const north = item.boundingbox ? parseFloat(item.boundingbox[1]) : parseFloat(item.lat) + 0.005;
    const west = item.boundingbox ? parseFloat(item.boundingbox[2]) : parseFloat(item.lon) - 0.005;
    const east = item.boundingbox ? parseFloat(item.boundingbox[3]) : parseFloat(item.lon) + 0.005;
    const lat = parseFloat(item.lat);
    const lng = parseFloat(item.lon);
    setOsmQuery("");
    setOsmResults([]);
    setPendingPlace({
      name: item.display_name,
      lat,
      lng,
      north,
      south,
      east,
      west,
      source: "osm"
    });
    setPendingCustomLabel("");
    setPendingStatus("visited");
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setVisitedHexes({});
    setPlaces([]);
    setPendingPlace(null);
    setMaps([]);
    setActiveMapId(null);
  };

  const invertedPositions = useMemo(() => [
    [
      [90, -180],
      [90, 180],
      [-90, 180],
      [-90, -180]
    ],
    ...Object.values(visitedHexes)
  ], [visitedHexes]);

  const visitedPlaceCircles = useMemo(() => {
    return generateAllPlaceCircles(places, visitedHexes, 'visited');
  }, [places, visitedHexes]);

  const unvisitedPlaceCircles = useMemo(() => {
    if (!showUnvisited) return [];
    return generateAllPlaceCircles(places, visitedHexes, 'yet_to_visit');
  }, [places, visitedHexes, showUnvisited]);

  const unionedVisitedRegions = useMemo(() => {
    return getUnionedCircleRegions(visitedPlaceCircles);
  }, [visitedPlaceCircles]);

  const unionedUnvisitedRegions = useMemo(() => {
    return getUnionedCircleRegions(unvisitedPlaceCircles);
  }, [unvisitedPlaceCircles]);

  const invertedCirclePositions = useMemo(() => [
    [
      [90, -180],
      [90, 180],
      [-90, 180],
      [-90, -180]
    ],
    ...unionedVisitedRegions.holes
  ], [unionedVisitedRegions]);

  const unvisitedHexBoundaries = useMemo(() => {
    if (!showUnvisited) return [];
    const hexMap = new Map();
    places.filter(p => p.status === 'yet_to_visit').forEach(place => {
      const hexes = getPlaceHexagons(place);
      hexes.forEach(h => {
        if (!hexMap.has(h)) {
          hexMap.set(h, {
            hex: h,
            boundary: cellToBoundary(h),
            place
          });
        }
      });
    });
    return Array.from(hexMap.values());
  }, [places, showUnvisited]);

  const visitedCount = useMemo(() => places.filter(p => p.status !== 'yet_to_visit').length, [places]);
  const wishlistCount = useMemo(() => places.filter(p => p.status === 'yet_to_visit').length, [places]);
  const filteredPlaces = useMemo(() => {
    let list = places;
    if (placesFilter === 'visited') list = places.filter(p => p.status !== 'yet_to_visit');
    else if (placesFilter === 'yet_to_visit') list = places.filter(p => p.status === 'yet_to_visit');
    return [...list].sort((a, b) => {
      const nameA = (a.custom_label || a.name || '').trim().toLowerCase();
      const nameB = (b.custom_label || b.name || '').trim().toLowerCase();
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });
  }, [places, placesFilter]);

  return (
    <>
      {!currentUser ? (
        <AuthPage onAuthSuccess={(user) => setCurrentUser(user)} />
      ) : (
        <div className="app-container">
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Syncing exploration data...</p>
        </div>
      )}
      
      {/* Sidebar Controls */}
      <div className="sidebar">
        <div className="brand">
          <Hexagon className="brand-icon" size={32} />
          <div>
            <h1>FOG OF LIFE MAP</h1>
            <p>Welcome, {currentUser.name}</p>
          </div>
        </div>

        <button className="auth-btn guest-btn" onClick={handleLogout} style={{marginBottom: '20px', padding: '8px', fontSize: '14px'}}>
          Log Out
        </button>

        {/* Map Projects Switcher */}
        <MapSwitcher
          maps={maps}
          activeMapId={activeMapId}
          onSelectMap={handleSelectMap}
          onCreateMap={handleCreateMap}
          onDuplicateMap={handleDuplicateMap}
          onUpdateMap={handleUpdateMap}
          onDeleteMap={handleDeleteMap}
          isLoading={isMapsLoading}
        />

        {hasGoogleKey ? (
          <div className="search-box">
            <Search size={18} color="#8b949e" />
            <Autocomplete
              apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
              onPlaceSelected={handlePlaceSelected}
              fields={["geometry", "formatted_address", "name", "place_id"]}
              placeholder="Search address, landmark, venue..."
              style={{
                background: 'transparent',
                border: 'none',
                color: 'white',
                flex: 1,
                outline: 'none',
                fontFamily: 'inherit',
                fontSize: '14px',
                margin: '0 10px'
              }}
            />
            <Plus size={18} color="#8b949e" style={{cursor: 'pointer'}} title="Quick Add" />
          </div>
        ) : (
          <div className="search-wrapper">
            <div className="search-box">
              <Search size={18} color="#8b949e" />
              <input
                type="text"
                value={osmQuery}
                onChange={(e) => handleOsmInputChange(e.target.value)}
                placeholder="Search address, landmark, venue (OSM)..."
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  flex: 1,
                  outline: 'none',
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  margin: '0 10px'
                }}
              />
              {isSearchingOsm ? (
                <Loader size={16} color="#58a6ff" style={{ animation: 'spin 1s linear infinite' }} />
              ) : osmQuery ? (
                <X size={16} color="#8b949e" style={{ cursor: 'pointer' }} onClick={() => { setOsmQuery(""); setOsmResults([]); }} />
              ) : null}
            </div>

            {osmResults.length > 0 && (
              <div className="osm-dropdown">
                {osmResults.map((item) => (
                  <div
                    key={item.place_id}
                    className="osm-item"
                    onClick={() => handleOsmPlaceSelected(item)}
                  >
                    <MapPin size={15} />
                    <span className="osm-item-text">{item.display_name}</span>
                    {item.type && <span className="osm-badge">{item.type}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Add Place Popover Card */}
        {pendingPlace && (
          <div className="add-place-card">
            <div className="add-place-header">
              <MapPin size={15} color="#58a6ff" />
              <span>Add Location</span>
            </div>
            <div className="add-place-location">{pendingPlace.name}</div>
            
            <div className="add-place-input-group">
              <label>Custom Label (Optional)</label>
              <input
                type="text"
                className="add-place-input"
                placeholder='e.g. First Date, Where David was born...'
                value={pendingCustomLabel}
                onChange={(e) => setPendingCustomLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') confirmAddPlace();
                  if (e.key === 'Escape') setPendingPlace(null);
                }}
                autoFocus
              />
            </div>

            <div className="status-toggle-group">
              <button
                type="button"
                className={`status-choice-btn ${pendingStatus === 'visited' ? 'active visited' : ''}`}
                onClick={() => setPendingStatus('visited')}
              >
                <span style={{ fontSize: '13px' }}>●</span> Visited
              </button>
              <button
                type="button"
                className={`status-choice-btn ${pendingStatus === 'yet_to_visit' ? 'active yet-to-visit' : ''}`}
                onClick={() => setPendingStatus('yet_to_visit')}
              >
                <span style={{ fontSize: '13px' }}>○</span> Bucket List
              </button>
            </div>

            <div className="add-place-actions">
              <button className="add-place-btn" onClick={confirmAddPlace} disabled={isLoading}>
                {isLoading ? 'Adding...' : 'Add to Map'}
              </button>
              <button className="cancel-place-btn" onClick={() => setPendingPlace(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="poster-studio-cta-section">
          <button className="poster-studio-cta-btn" onClick={() => setIsStudioOpen(true)}>
            <Sparkles size={18} />
            <span>Open Poster Studio</span>
          </button>
        </div>

        <div>
          <div className="section-title">Places & Wishlist</div>

          <div className="places-filter-tabs">
            <button 
              className={`filter-tab ${placesFilter === 'all' ? 'active' : ''}`}
              onClick={() => setPlacesFilter('all')}
            >
              All ({places.length})
            </button>
            <button 
              className={`filter-tab ${placesFilter === 'visited' ? 'active' : ''}`}
              onClick={() => setPlacesFilter('visited')}
            >
              Visited ({visitedCount})
            </button>
            <button 
              className={`filter-tab ${placesFilter === 'yet_to_visit' ? 'active' : ''}`}
              onClick={() => setPlacesFilter('yet_to_visit')}
            >
              Bucket List ({wishlistCount})
            </button>
          </div>

          <div className="places-list">
            {filteredPlaces.map((place) => {
              const isVisited = place.status !== 'yet_to_visit';
              const isEditing = editingPlaceId === place.id;

              return (
                <div key={place.id} className="place-item">
                  <div className="place-info" style={{ flex: 1, minWidth: 0, marginRight: '8px' }}>
                    <MapPin 
                      size={16} 
                      style={{ 
                        flexShrink: 0, 
                        cursor: 'pointer', 
                        color: isVisited ? '#ef4444' : '#3b82f6' 
                      }} 
                      onClick={() => focusPlace(place)} 
                      title="Center on map" 
                    />

                    {isEditing ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                        <input 
                          className="edit-input"
                          type="text" 
                          placeholder="Custom Label..."
                          value={editCustomLabel} 
                          onChange={(e) => setEditCustomLabel(e.target.value)} 
                          onKeyDown={(e) => e.key === 'Enter' && saveEditingPlace(place.id)} 
                          autoFocus 
                        />
                        <input 
                          className="edit-input"
                          style={{ fontSize: '11px', color: '#8b949e' }}
                          type="text" 
                          placeholder="Location Name..."
                          value={editPlaceName} 
                          onChange={(e) => setEditPlaceName(e.target.value)} 
                          onKeyDown={(e) => e.key === 'Enter' && saveEditingPlace(place.id)} 
                        />
                      </div>
                    ) : (
                      <div className="place-text-wrap" onDoubleClick={() => startEditing(place)}>
                        {place.custom_label ? (
                          <>
                            <span className="place-custom-label" title={place.custom_label}>
                              {place.custom_label}
                            </span>
                            <span className="place-subtext" title={place.name}>
                              {place.name}
                            </span>
                          </>
                        ) : (
                          <span className="place-name" title={place.name}>
                            {place.name}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="place-actions" style={{ alignItems: 'center' }}>
                    {!isEditing && (
                      <button
                        className={`status-badge-btn ${isVisited ? 'visited' : 'yet-to-visit'}`}
                        onClick={() => togglePlaceStatus(place)}
                        title={isVisited ? "Click to move to Bucket List" : "Click to mark as Visited & clear fog"}
                      >
                        {isVisited ? 'Visited' : 'Wishlist'}
                      </button>
                    )}

                    {isEditing ? (
                      <button className="action-btn" onClick={() => saveEditingPlace(place.id)} title="Save">
                        <Check size={16} color="#3fb950" />
                      </button>
                    ) : (
                      <button className="action-btn" onClick={() => startEditing(place)} title="Edit Label / Name">
                        <Edit2 size={15} />
                      </button>
                    )}

                    <button className="action-btn remove-btn" onClick={() => removePlace(place.id)} title="Remove Place">
                      <X size={15} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="view-settings-section" style={{ marginTop: '20px' }}>
          <div className="section-title">Map Settings</div>
          <div className="resolution-slider-container">
            <div className="resolution-header">
              <span>Map Opacity ({Math.round(opacity * 100)}%)</span>
            </div>
            <input 
              type="range" 
              min="0" 
              max="100" 
              value={Math.round(opacity * 100)}
              onChange={(e) => setOpacity(parseInt(e.target.value) / 100)}
              className="resolution-slider"
            />
          </div>
        </div>
      </div>

      {/* Main Map Viewport */}
      <div className="map-container">
        <MapContainer
          center={[20, 0]}
          zoom={3}
          style={{ width: '100%', height: '100%' }}
          zoomControl={false}
        >
          {/* Floating Map Presentation Selector */}
          <div className="leaflet-top leaflet-right" style={{ pointerEvents: 'auto', margin: '20px' }}>
            <div className="style-selector">
              <label htmlFor="map-style-select">Map Style: </label>
              <select 
                id="map-style-select" 
                value={mapStyle} 
                onChange={(e) => {
                  const newStyle = e.target.value;
                  setMapStyle(newStyle);
                  if (newStyle === "pins" || newStyle === "pins-label") {
                    setOpacity(0.0);
                  } else if (newStyle === "circled") {
                    setOpacity(0.8);
                  } else if (newStyle === "default") {
                    setOpacity(0.4);
                  }
                  if (activeMapId) {
                    setMaps(prev => prev.map(m => m.id === activeMapId ? { ...m, map_style: newStyle } : m));
                    fetch(`${API_URL}/maps/${activeMapId}`, {
                      method: "PUT",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ map_style: newStyle })
                    }).catch(err => console.error("Error saving map style:", err));
                  }
                }}
              >
                <option value="default">Default Fog</option>
                <option value="circled">Hand-Drawn Circles</option>
                <option value="pins">Map Pins</option>
                <option value="pins-label">Map Pins with Label</option>
              </select>

              <label className="show-unvisited-toggle" title="Show or hide unvisited bucket list places on the map">
                <input
                  type="checkbox"
                  checked={showUnvisited}
                  onChange={(e) => setShowUnvisited(e.target.checked)}
                />
                <span>Show Unvisited</span>
              </label>
            </div>
          </div>

          {mapStyle === "pins" || mapStyle === "pins-label" ? (
            <>
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                attribution='Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ'
                maxZoom={16}
              />
              {places.map((place) => {
                const isVisited = place.status !== 'yet_to_visit';
                if (!isVisited && !showUnvisited) return null;
                const center = getPlaceCenter(place);
                if (!center) return null;
                return (
                  <Marker
                    key={`pin-${place.id}`}
                    position={center}
                    icon={createPinIcon(place.name, mapStyle === "pins-label", place.custom_label, isVisited ? 'red' : 'blue')}
                    title={place.custom_label ? `${place.custom_label} (${place.name})` : place.name}
                    eventHandlers={{
                      click: () => focusPlace(place)
                    }}
                  />
                );
              })}
            </>
          ) : mapStyle === "circled" ? (
            <>
              {hasGoogleKey ? (
                <ReactLeafletGoogleLayer apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} type={'roadmap'} />
              ) : (
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; OpenStreetMap contributors'
                />
              )}
              {opacity > 0 && (
                <Polygon
                  positions={invertedCirclePositions}
                  pathOptions={{ color: 'transparent', fillColor: '#000000', fillOpacity: opacity, interactive: false }}
                />
              )}
              {/* Visited hand-drawn circles: red pen outlines */}
              {unionedVisitedRegions.strokes.map((stroke, idx) => (
                <React.Fragment key={`circle-stroke-${idx}`}>
                  {/* Subtle ink absorption bleed */}
                  <Polyline
                    positions={stroke}
                    pathOptions={{ color: '#b91c1c', weight: 4.5, opacity: 0.22, lineCap: 'round', lineJoin: 'round', interactive: false }}
                  />
                  {/* Human-drawn pen stroke */}
                  <Polyline
                    positions={stroke}
                    pathOptions={{ color: '#e63946', weight: 2.6, opacity: 0.92, lineCap: 'round', lineJoin: 'round', interactive: false }}
                  />
                </React.Fragment>
              ))}
              {/* Unvisited circles: blue hand-drawn circles, opaque following map opacity */}
              {showUnvisited && unionedUnvisitedRegions.holes.map((hole, idx) => (
                <Polygon
                  key={`unvisited-circle-fill-${idx}`}
                  positions={hole}
                  pathOptions={{
                    color: 'transparent',
                    fillColor: '#1d4ed8',
                    fillOpacity: Math.min(opacity * 0.35, 0.35),
                    interactive: false
                  }}
                />
              ))}
              {showUnvisited && unionedUnvisitedRegions.strokes.map((stroke, idx) => (
                <React.Fragment key={`unvisited-circle-stroke-${idx}`}>
                  {/* Subtle blue ink bleed */}
                  <Polyline
                    positions={stroke}
                    pathOptions={{ color: '#1d4ed8', weight: 4.5, opacity: 0.25, lineCap: 'round', lineJoin: 'round', interactive: false }}
                  />
                  {/* Human-drawn blue pen stroke */}
                  <Polyline
                    positions={stroke}
                    pathOptions={{ color: '#3b82f6', weight: 2.6, opacity: 0.95, lineCap: 'round', lineJoin: 'round', interactive: false }}
                  />
                </React.Fragment>
              ))}
            </>
          ) : (
            /* default (Default Fog) */
            <>
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                attribution='Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ'
                maxZoom={16}
              />
              {/* Visited hexagons (red color matching visited pins) */}
              {Object.entries(visitedHexes).map(([h3Index, boundary]) => (
                <Polygon
                  key={`fill-${h3Index}`}
                  positions={boundary}
                  pathOptions={{ color: 'rgba(239, 68, 68, 0.35)', weight: 0.8, fillColor: '#ef4444', fillOpacity: 0.25, interactive: false }}
                />
              ))}
              {/* Inverted dark fog mask cutout around visited hexes */}
              <Polygon
                positions={invertedPositions}
                pathOptions={{ color: '#ef4444', weight: 1.2, fillColor: '#000000', fillOpacity: opacity, interactive: false }}
              />
              {/* Unvisited mini hexagons (blue color matching unvisited pins, veiled under fog) */}
              {showUnvisited && unvisitedHexBoundaries.map(({ hex, boundary, place }) => (
                <Polygon
                  key={`unvisited-hex-${place.id}-${hex}`}
                  positions={boundary}
                  pathOptions={{
                    color: '#3b82f6',
                    weight: 1.3,
                    opacity: 0.9,
                    fillColor: '#1d4ed8',
                    fillOpacity: Math.min(opacity * 0.35, 0.35)
                  }}
                  eventHandlers={{
                    click: () => focusPlace(place)
                  }}
                />
              ))}
            </>
          )}
          
          {/* Controls flying to map locations */}
          <MapController targetBounds={mapBounds} />
        </MapContainer>
      </div>

          {isStudioOpen && (
            <PosterStudio
              visitedHexes={visitedHexes}
              places={places}
              currentUser={currentUser}
              activeMap={activeMap}
              onClose={() => setIsStudioOpen(false)}
              initialMapStyle={mapStyle}
              initialOpacity={opacity}
              initialShowUnvisited={showUnvisited}
            />
          )}
        </div>
      )}

      {/* Persistent Contact Widget accessible on all screens and states */}
      <ContactWidget currentUser={currentUser} />
    </>
  );
}

export default App;
